<!-- @catalog Select / dropdown. Single OR multi-select; multi-select renders removable chips out of the box. Use this instead of building a bespoke select or multi-select. -->
<template>
  <div class="tw-flex tw-flex-col tw-gap-2" :class="$attrs.class">
    <c-label v-if="label" :label :tooltip />

    <q-select
      v-bind="forwardedAttrs"
      ref="selectRef"
      :outlined="!borderless"
      :borderless
      :placeholder="placeholder"
      popup-content-class="tw-rounded-lg tw-border tw-border-gray-200 tw-p-2 !tw-shadow-lg"
      transition-show="jump-down"
      transition-hide="jump-up"
      @update:model-value="onUpdateModelValue"
    >
      <!-- Default checkbox option for multi-select with chips -->
      <template
        v-if="useDefaultMultiSlots && !$slots.option"
        #option="{ itemProps, opt, selected, toggleOption }"
      >
        <q-item v-bind="itemProps" :disable="opt.disable">
          <q-item-section side>
            <c-checkbox
              :model-value="selected"
              :disable="opt.disable"
              borderless
              @update:model-value="toggleOption(opt)"
            />
          </q-item-section>

          <q-item-section>
            <div class="tw-inline-flex tw-items-center tw-gap-1.5">
              <span>{{ optLabel(opt) }}</span>

              <c-tooltip-standalone v-if="opt.description" size="14px">
                {{ opt.description }}
              </c-tooltip-standalone>
            </div>
          </q-item-section>
        </q-item>
      </template>

      <!-- Default chip for multi-select with chips -->
      <template
        v-if="useDefaultMultiSlots && !$slots['selected-item']"
        #selected-item="{ opt, removeAtIndex, index }"
      >
        <c-input-chip
          :label="optLabel(opt)"
          :removable="!opt.disable"
          :color="chipColor"
          :tooltip="optLabel(opt)"
          @remove="removeAtIndex(index)"
        />
      </template>

      <!-- Muted placeholder shown when nothing is selected. QSelect has no native
         placeholder unless `use-input` is set, so we render one in the selection
         slot for the common non-input select. -->
      <template v-if="showPlaceholder" #selected>
        <span class="tw-text-gray-400">{{ placeholder }}</span>
      </template>

      <!-- Default empty state, so every dropdown shows the same message without
         boilerplate. Callers can still override with their own #no-option slot. -->
      <template v-if="!$slots['no-option']" #no-option>
        <div
          class="c-input-select__no-option tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500"
        >
          {{ noOptionLabel }}
        </div>
      </template>

      <!-- Pass through all custom slots -->
      <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
        <slot :name="slot" v-bind="scope || {}" />
      </template>
    </q-select>
  </div>
</template>

<script setup>
import { computed, useAttrs, useSlots, useTemplateRef } from "vue";
import CInputChip from "shared/components/wrappers/badge/CInputChip.vue";

// We forward attrs explicitly (minus the v-model update handler, which we
// intercept), so we disable the automatic single-root fallthrough to avoid
// binding the parent's onUpdate:modelValue twice.
defineOptions({ inheritAttrs: false });

const props = defineProps({
  label: {
    type: String,
  },
  tooltip: {
    type: [String, null],
    default: () => "",
  },
  borderless: {
    type: Boolean,
    default: false,
  },
  chipColor: {
    type: String,
    default: "gray",
  },
  placeholder: {
    type: String,
    default: "",
  },
  noOptionLabel: {
    type: String,
    default: "Geen resultaten gevonden",
  },
});

const attrs = useAttrs();
const slots = useSlots();

// Mirrors Quasar's option-label resolution for our overridden #option /
// #selected-item slots: callers can still pass option-label="full_name"
// or a function and the chips/items will render the right field.
const optLabel = (opt) => {
  if (typeof opt === "string" || typeof opt === "number") {
    return opt;
  }

  if (opt == null) {
    return "";
  }

  const optionLabel = attrs["option-label"];

  if (typeof optionLabel === "function") {
    return optionLabel(opt);
  }

  if (typeof optionLabel === "string" && optionLabel in opt) {
    return opt[optionLabel];
  }

  return opt.label;
};

// Resolves a model entry back to its option's disable flag. With emit-value the
// entry is the option's value (respecting a string `option-value`); otherwise
// it's the option object itself.
const isLockedEntry = (entry) => {
  if (entry !== null && typeof entry === "object") {
    return entry.disable === true;
  }

  const options = attrs.options;

  if (Array.isArray(options) === false) {
    return false;
  }

  const valueKey = attrs["option-value"] ?? "value";

  return options.find((opt) => opt?.[valueKey] === entry)?.disable === true;
};

// A caller binding both v-model and an explicit @update:model-value on this
// component (directly, or via a v-bind object like bindError() that injects
// its own onUpdate:modelValue) makes Vue merge the two listeners into an
// array for this attr instead of a single function.
const callParentUpdate = (value) => {
  const handler = attrs["onUpdate:modelValue"];

  if (Array.isArray(handler)) {
    handler.forEach((fn) => fn(value));
  } else {
    handler?.(value);
  }
};

// Guards every removal path (chip ✕, Backspace, clear): Quasar removes items
// unconditionally, so if the update drops a disabled (locked) option we veto it
// by re-emitting the previous selection. Quasar changes one item per
// interaction, so restoring the previous array both blocks the removal and
// preserves chip order.
const onUpdateModelValue = (value) => {
  const previous = attrs.modelValue ?? attrs["model-value"];

  const droppedLocked =
    Array.isArray(value) &&
    Array.isArray(previous) &&
    previous.some(
      (entry) =>
        value.includes(entry) === false && isLockedEntry(entry) === true,
    );

  callParentUpdate(droppedLocked === true ? previous.slice() : value);
};

// Forward everything to QSelect except the parent's v-model update handler,
// which we intercept above and re-emit (optionally corrected) via callParentUpdate.
const forwardedAttrs = computed(() => {
  const { "onUpdate:modelValue": _omit, ...rest } = attrs;

  return rest;
});

const useDefaultMultiSlots = computed(
  () =>
    attrs.multiple !== undefined &&
    attrs.multiple !== false &&
    attrs["use-chips"] !== undefined &&
    attrs["use-chips"] !== false,
);

const isUsingInput = computed(() => {
  const useInput = attrs["use-input"] ?? attrs.useInput;

  return useInput !== undefined && useInput !== false;
});

const hasSelection = computed(() => {
  const value = attrs.modelValue ?? attrs["model-value"];

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== null && value !== undefined && value !== "";
});

// QSelect only shows its native placeholder when `use-input` is set (otherwise
// there is no text input to host it). For a plain select we instead render the
// placeholder ourselves via the #selected slot when nothing is selected. We skip
// it when the caller provides their own #selected slot, to not override it.
const showPlaceholder = computed(
  () =>
    props.placeholder !== "" &&
    isUsingInput.value === false &&
    hasSelection.value === false &&
    slots.selected === undefined,
);

// Refs
const selectRef = useTemplateRef("selectRef");

// Expose
defineExpose({
  selectRef,
});
</script>

<style lang="scss">
label.q-field {
  .q-field__control-container input[required] + .q-field__label:after {
    content: " *";
  }
}

label.q-select.q-field--auto-height {
  .q-field__control {
    min-height: 40px !important;
  }

  .q-field__native {
    min-height: 40px !important;
    @apply tw-gap-2 tw-py-2;

    // QSelect always renders the selection as a span, even while nothing is picked; with
    // `use-input` the search input follows it. The gap above (there for chips) then sits
    // between an empty span and the input, indenting the placeholder by 8px. Nothing to
    // show, nothing to space.
    > span.ellipsis:empty {
      display: none;
    }
  }

  .q-field__marginal {
    height: auto !important;
    min-height: 40px;
  }
}

// .q-field--auto-height .q-field__control, .q-field--auto-height .q-field__native
</style>
